// Libraries
import React, { FC, useCallback } from 'react';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Avatar, Container, Typography } from '@mui/material';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { AxiosError } from 'axios';
import { red } from '@mui/material/colors';

// Components
import SimpleModal from '../Modals/SimpleModal';
import Form from '../Form/Form';
import { registrationFields, SUBMIT_BTN_TYPE } from '../Form/form.utils';
import { FormField } from '../../lib/interfaces/Form';

// Utils
import {
	setActivationModalOpen,
	setForgotPasswordModalOpen,
	setLoginModalOpen,
	setRegistrationModalOpen,
} from '../../redux/reducers/ui/modalsReducer';
import { registration, googleLogin } from '../../api/apis';
import {
	UserRegistrationDTO,
	UserGoogleLogin,
	UserRO,
} from '../../lib/interfaces/User';
import { useAppDispatch } from '../../hooks/redux';

// CSS
import './AuthModal.scss';
import reactQueryKeys from '../../reactQuery/reactQueryKeys';
import { LANGUAGES } from '../../lib/consts/languages';
import paths from '../../lib/consts/paths';

const RegistrationModal: FC = () => {
	const dispatch = useAppDispatch();
	const { t, i18n } = useTranslation();
	const queryClient = useQueryClient();
	const registrationFormFields = registrationFields.map((field: FormField) => {
		if (field.label) {
			field.label = t(field.label);
		}
		return field;
	});
	const navigate = useNavigate();
	const currentLocation = useLocation();
	const registrationMutation = useMutation<
		UserRO,
		AxiosError<any>,
		UserRegistrationDTO
	>((data) => registration(data));

	const googleLoginMutation = useMutation<UserRO, AxiosError, UserGoogleLogin>(
		(data: UserGoogleLogin) => googleLogin(data),
	);

	const handleClose = useCallback(() => {
		dispatch(setRegistrationModalOpen(false));
	}, [dispatch]);

	const handleOnSuccessLogin = (data: UserRO, isSocial = false) => {
		localStorage.setItem('currentUser', JSON.stringify(data.user));
		localStorage.setItem('token', JSON.stringify(data.token));
		queryClient.setQueryData(reactQueryKeys.userMe, () => ({ ...data.user }));
		handleClose();
		if (isSocial) {
			// TODO: navigate to courses if user is on home page
			const matches = matchRoutes([{ path: '/' }], currentLocation);
			if (matches) {
				navigate(paths.plans);
			}
		} else {
			dispatch(setActivationModalOpen(true));
		}
	};

	const handleSubmit = (formValues: UserRegistrationDTO) => {
		const values = {
			...formValues,
			language: i18n.language as LANGUAGES,
		};
		registrationMutation.mutate(values, {
			onSuccess(data: UserRO) {
				handleOnSuccessLogin(data);
			},
		});
	};

	const handleForgotPasswordClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(setRegistrationModalOpen(false));
		dispatch(setForgotPasswordModalOpen(true));
	};

	const handleLoginFormClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(setRegistrationModalOpen(false));
		dispatch(setLoginModalOpen(true));
	};

	const handleGoogleLoginSuccess = (credentialResponse: CredentialResponse) => {
		if (credentialResponse.credential) {
			googleLoginMutation.mutate(
				{
					token: credentialResponse.credential,
					language: i18n.language as LANGUAGES,
				},
				{
					onSuccess(data) {
						handleOnSuccessLogin(data, true);
					},
				},
			);
		}
	};

	const content: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar className="AuthModal__avatar">
						<AppRegistrationIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						{t('register')}
					</Typography>
					{registrationMutation.isError && (
						<>
							<Typography color={red['500']}>
								{registrationMutation.error?.response?.data?.message}
							</Typography>
							<Typography className="AuthModal__extra-links-wrapper">
								<a href="loginForm" onClick={(e) => handleLoginFormClick(e)}>
									{t('login')}
								</a>{' '}
								{t('or')}{' '}
								<a href="forgotPassword" onClick={(e) => handleForgotPasswordClick(e)}>
									{t('passwordRecovery')}
								</a>
							</Typography>
						</>
					)}
				</div>
				<div className="AuthModal__social">
					<GoogleLogin onSuccess={handleGoogleLoginSuccess} onError={() => {}} />
				</div>
				<div className="AuthModal__divider">{t('or')}</div>
				<Form
					fields={registrationFormFields}
					onSubmit={handleSubmit}
					saveButtonName={t('register')}
					isLoading={registrationMutation.isLoading}
					saveButtonStyle={SUBMIT_BTN_TYPE.FULL}
					fullWidth
				/>
			</div>
		</Container>
	);

	return <SimpleModal handleClose={handleClose} content={content} hideActions />;
};

export default RegistrationModal;
